import React, { useEffect, useMemo, useState } from "react";
import {
    Plus,
    Pencil,
    Trash2,
    Upload,
    Loader2,
    ExternalLink,
    FileText,
    BookOpen,
    Video,
    Download,
    HelpCircle,
    Eye,
    EyeOff,
    CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TYPES = [
    { key: "article", label: "Articles", icon: FileText, help: "Editorial pieces for thoughtful leaders." },
    { key: "research", label: "Research", icon: BookOpen, help: "Working papers & studies (PDF)." },
    { key: "video", label: "Videos", icon: Video, help: "YouTube / Vimeo URLs." },
    { key: "download", label: "Downloads", icon: Download, help: "PDFs for readers to take away." },
    { key: "faq", label: "FAQs", icon: HelpCircle, help: "Question + Answer pairs." },
];

const emptyForm = {
    type: "article",
    title: "",
    subtitle: "",
    body: "",
    external_url: "",
    file_url: "",
    read_time: "",
    published: true,
    order: 0,
};

const ContentManager = () => {
    const [items, setItems] = useState([]);
    const [activeType, setActiveType] = useState("article");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // { id?, ...form }
    const [deleteId, setDeleteId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await adminApi.get("/admin/content");
            setItems(res.data);
        } catch (e) {
            toast.error("Could not load content.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const filtered = useMemo(
        () => items.filter((i) => i.type === activeType),
        [items, activeType],
    );

    const startCreate = () => setEditing({ ...emptyForm, type: activeType });
    const startEdit = (item) => setEditing({ ...item });
    const closeEdit = () => setEditing(null);

    const save = async () => {
        try {
            const payload = {
                type: editing.type,
                title: (editing.title || "").trim(),
                subtitle: (editing.subtitle || "").trim(),
                body: (editing.body || "").trim(),
                external_url: (editing.external_url || "").trim(),
                file_url: (editing.file_url || "").trim(),
                read_time: (editing.read_time || "").trim(),
                published: !!editing.published,
                order: Number.isFinite(+editing.order) ? +editing.order : 0,
            };
            if (!payload.title) {
                toast.error("Title is required.");
                return;
            }
            if (editing.id) {
                await adminApi.put(`/admin/content/${editing.id}`, payload);
            } else {
                await adminApi.post("/admin/content", payload);
            }
            toast.success(editing.id ? "Updated." : "Created.");
            closeEdit();
            load();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Save failed. Please try again.",
            );
        }
    };

    const togglePublished = async (item) => {
        try {
            await adminApi.put(`/admin/content/${item.id}`, {
                type: item.type,
                title: item.title,
                subtitle: item.subtitle || null,
                body: item.body || null,
                external_url: item.external_url || null,
                file_url: item.file_url || null,
                read_time: item.read_time || null,
                published: !item.published,
                order: item.order || 0,
            });
            toast.success(item.published ? "Set to draft." : "Published.");
            load();
        } catch (e) {
            toast.error("Could not update status.");
        }
    };

    const doDelete = async () => {
        try {
            await adminApi.delete(`/admin/content/${deleteId}`);
            toast.success("Deleted.");
            setDeleteId(null);
            load();
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    return (
        <div data-testid="content-manager" className="space-y-6">
            {/* Type filter chips */}
            <div className="ih-card p-4">
                <div className="flex flex-wrap gap-2" data-testid="content-type-tabs">
                    {TYPES.map(({ key, label, icon: Icon }) => {
                        const count = items.filter((i) => i.type === key).length;
                        const active = activeType === key;
                        return (
                            <button
                                key={key}
                                onClick={() => setActiveType(key)}
                                data-testid={`content-tab-${key}`}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-poppins text-xs uppercase tracking-[0.12em] font-medium transition ${
                                    active
                                        ? "bg-indigo_brand text-white"
                                        : "bg-white border border-surface-border/70 text-indigo_brand hover:bg-surface"
                                }`}
                            >
                                <Icon size={14} />
                                {label}
                                <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] ${active ? "bg-white/20 text-white" : "bg-surface text-[#4A5568]"}`}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* List */}
            <div className="ih-card p-6 sm:p-8" data-testid="content-list-card">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                    <div>
                        <p className="ih-eyebrow">
                            {TYPES.find((t) => t.key === activeType)?.label}
                        </p>
                        <h2 className="mt-1 font-montserrat font-bold text-indigo_brand text-xl">
                            {TYPES.find((t) => t.key === activeType)?.help}
                        </h2>
                    </div>
                    <button
                        onClick={startCreate}
                        data-testid="content-add"
                        className="ih-btn-primary"
                    >
                        <Plus size={16} /> Add New
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-12">
                        <Loader2 className="animate-spin mx-auto text-indigo_brand" size={22} />
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16">
                        <p className="font-opensans text-[#4A5568] text-sm">
                            No {activeType} items yet. Click <strong>Add New</strong> to publish your first.
                        </p>
                    </div>
                ) : (
                    <ul className="divide-y divide-surface-border/50">
                        {filtered.map((item) => (
                            <li
                                key={item.id}
                                data-testid={`content-item-${item.id}`}
                                className="py-4 flex flex-col sm:flex-row sm:items-center gap-4"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-raleway font-semibold text-indigo_brand text-base leading-snug">
                                            {item.title}
                                        </p>
                                        {item.published ? (
                                            <Badge className="bg-green_brand text-white hover:bg-green_brand text-[10px]">
                                                <CheckCircle2 size={11} className="mr-1" /> Published
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="border-[#4A5568] text-[#4A5568] text-[10px]">
                                                Draft
                                            </Badge>
                                        )}
                                    </div>
                                    {item.subtitle && (
                                        <p className="mt-1 font-opensans text-sm text-[#4A5568] leading-relaxed line-clamp-2">
                                            {item.subtitle}
                                        </p>
                                    )}
                                    <div className="mt-2 flex flex-wrap gap-3 font-opensans text-[11px] text-[#4A5568]">
                                        {item.read_time && <span>{item.read_time}</span>}
                                        {item.external_url && (
                                            <a href={item.external_url} target="_blank" rel="noreferrer" className="text-green_brand hover:underline inline-flex items-center gap-1">
                                                Link <ExternalLink size={10} />
                                            </a>
                                        )}
                                        {item.file_url && (
                                            <a href={item.file_url} target="_blank" rel="noreferrer" className="text-green_brand hover:underline inline-flex items-center gap-1">
                                                PDF <ExternalLink size={10} />
                                            </a>
                                        )}
                                        {typeof item.order === "number" && item.order !== 0 && <span>Order: {item.order}</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => togglePublished(item)}
                                        data-testid={`content-toggle-${item.id}`}
                                        title={item.published ? "Unpublish" : "Publish"}
                                        className="p-2 rounded-md hover:bg-surface transition text-indigo_brand"
                                    >
                                        {item.published ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                    <button
                                        onClick={() => startEdit(item)}
                                        data-testid={`content-edit-${item.id}`}
                                        title="Edit"
                                        className="p-2 rounded-md hover:bg-surface transition text-indigo_brand"
                                    >
                                        <Pencil size={16} />
                                    </button>
                                    <button
                                        onClick={() => setDeleteId(item.id)}
                                        data-testid={`content-delete-${item.id}`}
                                        title="Delete"
                                        className="p-2 rounded-md hover:bg-red-50 hover:text-red-600 transition text-indigo_brand"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {editing && (
                <ContentEditor
                    value={editing}
                    onChange={setEditing}
                    onCancel={closeEdit}
                    onSave={save}
                />
            )}

            <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this item?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes the content permanently. Any attached PDF is also
                            deleted. This can&rsquo;t be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="content-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={doDelete}
                            data-testid="content-delete-confirm"
                            className="bg-red-600 hover:bg-red-700"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

// ---- Editor Dialog ----

const ContentEditor = ({ value, onChange, onCancel, onSave }) => {
    const [uploading, setUploading] = useState(false);
    const set = (k, v) => onChange({ ...value, [k]: v });
    const isEdit = !!value.id;

    const isFaq = value.type === "faq";
    const isVideo = value.type === "video";
    const isFilebacked = value.type === "download" || value.type === "research";
    const isArticle = value.type === "article";

    const uploadPdf = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const res = await adminApi.post("/admin/content/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            set("file_url", res.data.file_url);
            toast.success(`Uploaded ${res.data.filename}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onCancel()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-montserrat font-bold text-indigo_brand text-2xl">
                        {isEdit ? "Edit" : "New"} {TYPES.find((t) => t.key === value.type)?.label.slice(0, -1)}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-5 py-2">
                    <div>
                        <Label className="font-raleway font-semibold text-indigo_brand text-sm">
                            {isFaq ? "Question *" : "Title *"}
                        </Label>
                        <Input
                            data-testid="editor-title"
                            value={value.title}
                            onChange={(e) => set("title", e.target.value)}
                            placeholder={isFaq ? "What is the HUMAN+ Framework?" : "Title of the piece"}
                            className="mt-2 h-11"
                        />
                    </div>

                    {!isFaq && (
                        <div>
                            <Label className="font-raleway font-semibold text-indigo_brand text-sm">
                                Short description
                            </Label>
                            <Input
                                data-testid="editor-subtitle"
                                value={value.subtitle}
                                onChange={(e) => set("subtitle", e.target.value)}
                                placeholder="One-line description shown on the cards."
                                className="mt-2 h-11"
                            />
                        </div>
                    )}

                    {(isArticle || isFaq) && (
                        <div>
                            <Label className="font-raleway font-semibold text-indigo_brand text-sm">
                                {isFaq ? "Answer *" : "Body"}
                            </Label>
                            <Textarea
                                data-testid="editor-body"
                                value={value.body}
                                onChange={(e) => set("body", e.target.value)}
                                rows={isFaq ? 5 : 10}
                                placeholder={isFaq ? "The answer readers see when they open this FAQ." : "Article body. Line breaks are respected."}
                                className="mt-2"
                            />
                        </div>
                    )}

                    {isVideo && (
                        <div>
                            <Label className="font-raleway font-semibold text-indigo_brand text-sm">
                                Video URL (YouTube / Vimeo) *
                            </Label>
                            <Input
                                data-testid="editor-external-url"
                                value={value.external_url}
                                onChange={(e) => set("external_url", e.target.value)}
                                placeholder="https://www.youtube.com/watch?v=…"
                                className="mt-2 h-11"
                            />
                        </div>
                    )}

                    {isArticle && (
                        <div>
                            <Label className="font-raleway font-semibold text-indigo_brand text-sm">
                                Optional external link
                            </Label>
                            <Input
                                data-testid="editor-external-url"
                                value={value.external_url}
                                onChange={(e) => set("external_url", e.target.value)}
                                placeholder="Link to the full piece if published elsewhere."
                                className="mt-2 h-11"
                            />
                        </div>
                    )}

                    {isFilebacked && (
                        <div>
                            <Label className="font-raleway font-semibold text-indigo_brand text-sm">PDF file *</Label>
                            <div className="mt-2 flex flex-col sm:flex-row gap-3 sm:items-center">
                                <label
                                    className={`inline-flex items-center gap-2 ih-btn-secondary cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                                    data-testid="editor-upload-btn"
                                >
                                    {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                                    {uploading ? "Uploading…" : value.file_url ? "Replace PDF" : "Upload PDF"}
                                    <input
                                        type="file"
                                        accept="application/pdf,.pdf"
                                        className="hidden"
                                        data-testid="editor-file-input"
                                        onChange={(e) => uploadPdf(e.target.files?.[0])}
                                    />
                                </label>
                                {value.file_url && (
                                    <a
                                        href={value.file_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="ih-btn-ghost text-xs"
                                    >
                                        View current PDF <ExternalLink size={12} />
                                    </a>
                                )}
                            </div>
                        </div>
                    )}

                    {isArticle && (
                        <div>
                            <Label className="font-raleway font-semibold text-indigo_brand text-sm">
                                Read time (optional)
                            </Label>
                            <Input
                                data-testid="editor-read-time"
                                value={value.read_time}
                                onChange={(e) => set("read_time", e.target.value)}
                                placeholder="6 min read"
                                className="mt-2 h-11"
                            />
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label className="font-raleway font-semibold text-indigo_brand text-sm">
                                Display order
                            </Label>
                            <Input
                                data-testid="editor-order"
                                type="number"
                                value={value.order}
                                onChange={(e) => set("order", e.target.value)}
                                className="mt-2 h-11"
                            />
                            <p className="mt-1 text-[11px] text-[#4A5568]">Lower numbers appear first.</p>
                        </div>
                        <div>
                            <Label className="font-raleway font-semibold text-indigo_brand text-sm">
                                Published
                            </Label>
                            <div className="mt-2 flex items-center gap-3 h-11">
                                <Switch
                                    checked={!!value.published}
                                    onCheckedChange={(v) => set("published", v)}
                                    data-testid="editor-published"
                                />
                                <span className="font-opensans text-sm text-[#4A5568]">
                                    {value.published ? "Live on /resources" : "Hidden — saved as draft"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <DialogFooter className="pt-4">
                    <button onClick={onCancel} data-testid="editor-cancel" className="ih-btn-secondary">
                        Cancel
                    </button>
                    <button onClick={onSave} data-testid="editor-save" className="ih-btn-primary">
                        {isEdit ? "Save Changes" : "Create"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ContentManager;
